import { useLazyQuery } from "@apollo/client/react"
import { DateTime } from "luxon"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import { createPublicReservationValidationSchema } from "@/forms/public_reservation"
import { PublicVenueDocument, type GetPublicReservationQuery } from "@/graphql/graphql"
import { type ReservationFormValues } from "@/pages/public/venue"
import {
  buildBookingPolicySectionFromRules,
  buildBookingPolicyTiles,
  parseBookingPolicyTileKinds,
} from "@/shared/booking_policy"
import { showErrorToast } from "@/shared/toast"

type Reservation = NonNullable<GetPublicReservationQuery["public"]["reservation"]>

type Props = {
  reservation: Reservation | null | undefined
  reservationId: string | undefined
  timezone: string | undefined
  token: string
}

export function useModifyReservationFlow({ reservation, reservationId, timezone, token }: Props) {
  const navigate = useNavigate()
  const [modifyModalOpen, setModifyModalOpen] = useState(false)
  const [fetchVenue, { data: venueData, loading: venueLoading }] = useLazyQuery(PublicVenueDocument)

  const fullVenue = venueData?.public?.venue

  const handleModifyClick = useCallback(() => {
    if (!reservation?.venue?.slug) return

    void fetchVenue({ variables: { slug: reservation.venue.slug } })
      .then(() => {
        setModifyModalOpen(true)
      })
      .catch((e) => {
        showErrorToast()
        console.error(e)
      })
  }, [fetchVenue, reservation?.venue?.slug])

  const handleModifyClose = useCallback(() => {
    setModifyModalOpen(false)
  }, [])

  const handleModifyComplete = useCallback(
    (newReservationId: string) => {
      setModifyModalOpen(false)
      navigate(`/r/${newReservationId}`)
    },
    [navigate],
  )

  const modifyInitialValues: ReservationFormValues | null = useMemo(() => {
    if (!reservation || !timezone) return null

    return {
      date: DateTime.fromISO(reservation.startTime).setZone(timezone).startOf("day"),
      peopleAmount: reservation.peopleAmount || 2,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      tableId: reservation.tableId,
      name: reservation.name || "",
      email: reservation.email || "",
      phone: reservation.phone || "",
      note: reservation.customerNote || "",
      marketingConsent: false,
      termsAccepted: false,
    }
  }, [reservation, timezone])

  const modifyValidationSchema = useMemo(() => {
    if (!fullVenue) return null

    return createPublicReservationValidationSchema(fullVenue.reservationConfiguration, fullVenue.timezone, true)
  }, [fullVenue])

  const bookingPolicyTiles = useMemo(() => {
    if (!fullVenue) return []

    const available = parseBookingPolicyTileKinds(fullVenue.reservationConfiguration?.availableBookingPolicyTiles)
    const configured = parseBookingPolicyTileKinds(fullVenue.reservationConfiguration?.bookingPolicyTiles)

    return buildBookingPolicyTiles(
      {
        availableBookingPolicyTiles: available,
        bookingPolicyTiles: configured,
        minReservationTime: fullVenue.reservationConfiguration?.minReservationTime,
        maxReservationTime: fullVenue.reservationConfiguration?.maxReservationTime,
        noshowAutoCancelAfter: fullVenue.reservationConfiguration?.noshowAutoCancelAfter,
        largeGroupThreshold: fullVenue.reservationConfiguration?.largeGroupThreshold,
        cancellationDeadline: fullVenue.reservationConfiguration?.cancellationDeadline,
        phone: fullVenue.info?.phone,
        email: fullVenue.info?.email,
      },
      { includeDisabled: false, includeUnconfigured: false },
    )
  }, [fullVenue])

  const bookingPolicySection = useMemo(() => {
    if (!fullVenue) return null

    return buildBookingPolicySectionFromRules(fullVenue.reservationRuleSections)
  }, [fullVenue])

  const originalReservation = useMemo(() => {
    if (!reservation) return null

    return {
      id: reservationId,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      peopleAmount: reservation.peopleAmount || 2,
      tableId: reservation.tableId,
    }
  }, [reservation, reservationId])

  const venueLogoUrl = fullVenue?.images?.find((img) => img.kind === "venue_logo")?.url || null

  return {
    bookingPolicySection,
    bookingPolicyTiles,
    fullVenue,
    handleModifyClick,
    handleModifyClose,
    handleModifyComplete,
    modifyInitialValues,
    modifyModalOpen,
    modifyValidationSchema,
    originalReservation,
    token,
    venueLoading,
    venueLogoUrl,
  }
}
